import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import surplusSalesService from './surplusSales.service';

const initialState = {
  surplusSales: [],
  currentSurplusSale: null,
  loading: false,
  success: false,
  error: null,
  message: null,
  pagination: {
    total: 0,
    page: 1,
    totalPages: 1,
    limit: 20,
  },
  summary: {
    totalSurplus: 0,
    count: 0,
  },
};

// Fetch all surplus sales
export const fetchSurplusSales = createAsyncThunk(
  'surplusSales/fetchAll',
  async (params, thunkAPI) => {
    try {
      return await surplusSalesService.getAllSurplusSales(params);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Fetch single surplus sale details
export const fetchSurplusSaleDetails = createAsyncThunk(
  'surplusSales/fetchDetails',
  async (id, thunkAPI) => {
    try {
      return await surplusSalesService.getSurplusSaleDetails(id);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Add new surplus sale
export const addSurplusSale = createAsyncThunk(
  'surplusSales/add',
  async (data, thunkAPI) => {
    try {
      return await surplusSalesService.addSurplusSale(data);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update surplus sale
export const updateSurplusSale = createAsyncThunk(
  'surplusSales/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await surplusSalesService.updateSurplusSale({ id, data });
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete surplus sale
export const deleteSurplusSale = createAsyncThunk(
  'surplusSales/delete',
  async (id, thunkAPI) => {
    try {
      return await surplusSalesService.deleteSurplusSale(id);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const surplusSalesSlice = createSlice({
  name: 'surplusSales',
  initialState,
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.message = null;
    },
    clearCurrentSurplusSale: (state) => {
      state.currentSurplusSale = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Surplus Sales
      .addCase(fetchSurplusSales.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSurplusSales.fulfilled, (state, action) => {
        state.loading = false;
        state.surplusSales = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
        state.summary = action.payload.summary || initialState.summary;
      })
      .addCase(fetchSurplusSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Surplus Sale Details
      .addCase(fetchSurplusSaleDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSurplusSaleDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSurplusSale = action.payload.data;
      })
      .addCase(fetchSurplusSaleDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add Surplus Sale
      .addCase(addSurplusSale.pending, (state) => {
        state.loading = true;
      })
      .addCase(addSurplusSale.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(addSurplusSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Surplus Sale
      .addCase(updateSurplusSale.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSurplusSale.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(updateSurplusSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Surplus Sale
      .addCase(deleteSurplusSale.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSurplusSale.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(deleteSurplusSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { reset, clearCurrentSurplusSale } = surplusSalesSlice.actions;
export default surplusSalesSlice.reducer;
